import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { api, HttpError } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { deleteIntegration } from '@/lib/api/endpoints/integrations';
import { broadcastIntegrationDisconnect } from '@/modules/shared/integrationAccounts';
import type { IntegrationSyncErrorCode } from '@/common/components/IntegrationSyncStatusBadge';
import { formatLastSyncedAt } from '../flightUtils';
import type { SyncState } from '@xavior/contracts';
import { deriveCalendarSyncView } from './calendarSyncState';
import { readSessionPersisted, writeSessionPersisted } from '@/common/hooks/useSessionPersistedState';

/**
 * Calendar-account SYNC ORCHESTRATION (provider-agnostic: Google + Microsoft)
 * for the calendar `EventListModule` (issue #3279). Owns the connection-lifecycle
 * + account/calendar-toggle + sync-poll cluster. Hook-call ordering, dependency
 * arrays, ref identities, and the addEventListener/removeEventListener pairs for
 * the three custom events (`integration-oauth-connected`,
 * `integrationAccountToggled`, `integrationAccountDisconnected`) are invariant;
 * the wire contract is POST `/integrations/calendar/sync` (no `/v1`, no raw
 * fetch). Pinned by `EventListModule.syncContract.test.ts` +
 * `EventListModule.disconnectListener.test.ts`.
 *
 * `showOptionsMenu` stays owned by the parent (it's parent UI state); the hook
 * reads it to drive the auto-expand-on-menu-open effect.
 */

/**
 * A POST /sync job lives in the API's in-memory store and auto-expires after a
 * TTL. Once gone, the status GET returns 404 — there is no terminal event left
 * to wait for, so the poll loop must stop instead of spinning to its timeout.
 */
const isSyncJobExpired = (err: unknown): boolean =>
  err instanceof HttpError && err.status === 404;

/** A calendar account available from any provider (currently google/microsoft). */
export interface CalendarAccount {
  id: string;
  email: string;
  provider: string;
  enabledForModule?: boolean;
  enabledModules?: string[];
  isPrimary?: boolean;
  /** 'disabled' = Retain-disconnected (data kept, not syncing). */
  status?: 'active' | 'disabled';
  syncError?: string | null;
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncErrorAt?: string | null;
}

export interface UseCalendarSyncOptions {
  moduleId: string;
  token: string | null;
  conversationId: string;
  queryClient: QueryClient;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  /** Parent-owned: the gear/options menu open state (drives auto-expand). */
  showOptionsMenu: boolean;
}

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

/** Guards a stale/corrupt sessionStorage read against the `CalendarAccount[]` shape. */
export function isCalendarAccountArray(value: unknown): value is CalendarAccount[] {
  if (!Array.isArray(value)) return false;
  return value.every((v) => {
    if (typeof v !== 'object' || v === null) return false;
    const a = v as Record<string, unknown>;
    return typeof a.id === 'string' && typeof a.email === 'string' && typeof a.provider === 'string';
  });
}

export function useCalendarSync(opts: UseCalendarSyncOptions) {
  const { moduleId, token, conversationId, queryClient, showToast, showOptionsMenu } = opts;

  // (#5565) Connection/account status has no synchronous seed, unlike
  // calendarDate/timeFilter/calendarScope — it resets to disconnected/unknown
  // on every mount and is only corrected after refreshCalendarConnection()/
  // fetchCalendarAccounts() resolve (mount effect below). Since the module
  // pane unmounts/remounts on every switch away and back (keyed by
  // module.id), that round trip was visible on essentially every re-entry:
  // the Sync button and the account-scope dropdown popped in a beat after
  // the already-cached event grid. Seeding both from sessionStorage restores
  // the last-known snapshot on the FIRST render; the real fetch still runs
  // and reconciles silently. Kept as plain useState (not
  // useSessionPersistedState) — both setters are called directly all over
  // this file, and only a DIRECT useState setter is recognized as
  // referentially stable by react-hooks/exhaustive-deps.
  const calendarConnectedStorageKey = `xavior:calendar-connected:${moduleId}`;
  const calendarAccountsStorageKey = `xavior:calendar-accounts:${moduleId}`;
  const [calendarConnected, setCalendarConnected] = useState<boolean>(() =>
    readSessionPersisted<boolean>(calendarConnectedStorageKey, false, isBoolean),
  );
  const [skipCalendarPrompt, setSkipCalendarPrompt] = useState(
    () => { try { return localStorage.getItem(`xavior:skip-calendar-prompt:${moduleId}`) === '1'; } catch { return false; } },
  );
  const [isSyncingCalendar, setIsSyncingCalendar] = useState(false);
  const [showSyncExplanationModal, setShowSyncExplanationModal] = useState(false);
  const [calendarLastSyncedAt, setCalendarLastSyncedAt] = useState<string | null>(null);
  const [calendarSyncError, setCalendarSyncError] = useState<string | null>(null);
  const calendarLastSyncLabel = formatLastSyncedAt(calendarLastSyncedAt);
  const [calendarInitialSyncComplete, setCalendarInitialSyncComplete] = useState(false);

  const [calendarAccounts, setCalendarAccounts] = useState<CalendarAccount[]>(() =>
    readSessionPersisted<CalendarAccount[]>(calendarAccountsStorageKey, [], isCalendarAccountArray),
  );
  // #2761 — seeded to `true` so the reset effect can't observe a settled-false
  // state on the first render (before the fetch effect has had a chance to
  // run and flip it true). Without this seed the reset effect sees
  // `loadingAccounts === false` + `calendarAccounts === []` on the first paint and
  // wipes the persisted scope before the actual accounts arrive. The fetch
  // effect (or the `!token` early return path) flips this to `false` exactly
  // when the initial settle is complete, gating the reset effect correctly.
  // (#5565: this stays seeded `true` regardless of the new calendarAccounts
  // seed above — the reset-to-'all' effect below is unaffected since it's
  // gated on `loadingAccounts`, not on whether `calendarAccounts` is empty.)
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // (#5565) Write the reconciled snapshot back to sessionStorage on every
  // change so the NEXT mount seeds from the latest known-good values —
  // covers the initial fetch settling, connects, disconnects, and toggles.
  useEffect(() => {
    writeSessionPersisted(calendarConnectedStorageKey, calendarConnected);
  }, [calendarConnectedStorageKey, calendarConnected]);
  useEffect(() => {
    writeSessionPersisted(calendarAccountsStorageKey, calendarAccounts);
  }, [calendarAccountsStorageKey, calendarAccounts]);

  // #2726: Account-scope selector (mirrors Gmail's MailboxDropdown). 'all'
  // shows the union of every enabled account's calendars; an accountId scopes
  // the calendar grid (and the per-calendar checkbox list in the gear menu)
  // to that one account. Persisted per-module in localStorage so the
  // last-used scope survives page reloads.
  const calendarScopeStorageKey = `xavior:calendar-scope:${moduleId}`;
  const [calendarScope, setCalendarScopeState] = useState<'all' | string>(() => {
    try { return localStorage.getItem(calendarScopeStorageKey) || 'all'; } catch { return 'all'; }
  });
  const setCalendarScope = useCallback((next: 'all' | string) => {
    setCalendarScopeState(next);
    try { localStorage.setItem(calendarScopeStorageKey, next); } catch { /* ignore */ }
  }, [calendarScopeStorageKey]);
  // Auto-reset to 'all' if the selected account is no longer enabled.
  // `loadingAccounts` is seeded `true` (see above), so the first render is
  // gated until the fetch effect settles — that defends the persisted scope
  // from being wiped against an empty initial `calendarAccounts` array.
  useEffect(() => {
    if (calendarScope === 'all') return;
    if (loadingAccounts) return;
    const stillEnabled = calendarAccounts.some(a => a.id === calendarScope && a.enabledForModule);
    if (!stillEnabled) setCalendarScope('all');
  }, [calendarScope, calendarAccounts, loadingAccounts, setCalendarScope]);

  // Tracks accountIds whose `integrationAccountDisconnected` dispatch originated
  // from this component's handleDisconnectAccount. The listener uses this
  // set to skip re-invalidating events (the handler already does so inline)
  // and avoid an empty-list flash. The set is consumed on first read so a
  // stale entry can't suppress a later cross-component disconnect.
  const selfDispatchedDisconnectRef = useRef<Set<string>>(new Set());

  const fetchCalendarSyncStatus = useCallback(async () => {
    if (!token || !moduleId) return; // guard: never send ?moduleId=undefined
    try {
      const response = await api.http.get<SyncState>(
        `/integrations/calendar/sync-status?moduleId=${moduleId}`,
        { authToken: token },
      );
      const view = deriveCalendarSyncView(response.data);
      setCalendarInitialSyncComplete(view.initialSyncComplete);
    } catch (error) {
      console.error('Failed to fetch calendar sync status:', error);
    }
  }, [moduleId, token]);

  const refreshCalendarConnection = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.http.get<{
        connected: boolean;
        lastSyncedAt?: string;
        syncError?: string;
      }>(`/integrations/calendar/connection?moduleId=${moduleId}`, { authToken: token });
      const connected = response.data.connected;
      setCalendarConnected(connected);
      setCalendarLastSyncedAt(connected ? response.data.lastSyncedAt ?? null : null);
      setCalendarSyncError(connected ? response.data.syncError ?? null : null);
    } catch (error) {
      console.error('Failed to check calendar connection:', error);
    }
  }, [moduleId, token]);

  // Fetch all calendar accounts and check which ones are linked to this module
  // autoEnable: only true on first connect (OAuth callback), not on subsequent fetches
  // (#5565: moved above the disconnect/toggle listener below so that listener
  // can call it directly — a `const` declared later in the same function
  // scope can't be referenced from an earlier hook's dependency array
  // without hitting the temporal-dead-zone.)
  const fetchCalendarAccounts = useCallback(async (autoEnable = false, opts?: { silent?: boolean }) => {
    // No token — leave `loadingAccounts` at its seeded `true` so the reset
    // effect stays gated. Once a token arrives and a real fetch completes,
    // `loadingAccounts` will flip to false below and the reset effect can
    // make an authoritative decision against the real account list.
    if (!token) return;
    // (#5565 review) `silent: true` is for a BACKGROUND reconcile (the
    // cross-module disconnect/toggle listener below) — it must NOT toggle
    // `loadingAccounts`, or every cross-module integration event would flash
    // an already-open CalendarOptionsMenu's account list to "Loading
    // accounts…" even though nothing about THIS module's accounts changed.
    const silent = opts?.silent ?? false;
    if (!silent) setLoadingAccounts(true);
    try {
      const accountsResponse = await api.http.get<{ accounts: Array<{ id: string; email: string; provider: string; enabledForModule?: boolean; enabledModules?: string[]; isPrimary?: boolean; status?: 'active' | 'disabled'; syncError?: string | null; syncErrorCode?: IntegrationSyncErrorCode | null; syncErrorAt?: string | null }> }>(
        `/integrations/calendar/accounts?moduleId=${encodeURIComponent(moduleId)}`,
        { authToken: token }
      );

      let rawAccounts = accountsResponse.data.accounts;

      if (autoEnable) {
        const disabledAccounts = rawAccounts.filter(acc => !acc.enabledForModule);
        if (disabledAccounts.length > 0) {
          const scopeQuery = conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '';
          for (const acc of disabledAccounts) {
            try {
              await api.http.post(
                `/integrations/calendar/${acc.id}/modules/${moduleId}/enable${scopeQuery}`,
                {},
                { authToken: token }
              );
              const calResponse = await api.http.get<{ calendars: Array<{ id: string; name: string; primary: boolean; selected: boolean }> }>(
                `/integrations/calendar/${acc.id}/calendars?moduleId=${encodeURIComponent(moduleId)}`,
                { authToken: token }
              );
              for (const cal of calResponse.data.calendars) {
                const shouldBeSelected = cal.primary;
                if (cal.selected !== shouldBeSelected) {
                  await api.http.put(
                    `/integrations/calendar/${acc.id}/calendars/${encodeURIComponent(cal.id)}`,
                    { selected: shouldBeSelected, calendarName: cal.name, moduleId: moduleId },
                    { authToken: token }
                  );
                }
              }
            } catch (e) {
              console.error(`Failed to auto-enable account ${acc.email}:`, e);
            }
          }
          const updated = await api.http.get<{ accounts: Array<{ id: string; email: string; provider: string; enabledForModule?: boolean; enabledModules?: string[]; isPrimary?: boolean; status?: 'active' | 'disabled'; syncError?: string | null; syncErrorCode?: IntegrationSyncErrorCode | null; syncErrorAt?: string | null }> }>(
            `/integrations/calendar/accounts?moduleId=${encodeURIComponent(moduleId)}`,
            { authToken: token }
          );
          rawAccounts = updated.data.accounts;
        }
      }

      const accounts: CalendarAccount[] = rawAccounts.map(acc => ({
        id: acc.id,
        email: acc.email,
        provider: acc.provider ?? 'google',
        enabledForModule: acc.enabledForModule !== false,
        enabledModules: acc.enabledModules,
        isPrimary: acc.isPrimary,
        status: acc.status ?? 'active',
        syncError: acc.syncError ?? null,
        syncErrorCode: acc.syncErrorCode ?? null,
        syncErrorAt: acc.syncErrorAt ?? null,
      }));

      setCalendarAccounts(accounts);

      if (accounts.some(a => a.enabledForModule)) {
        setCalendarConnected(true);
      }
    } catch (error) {
      console.error('Failed to fetch calendar accounts:', error);
      // (#5565 review) Do NOT reset to `[]` here. Pre-#5565 this was
      // in-memory-only and harmless; now the write-back effect PERSISTS
      // `calendarAccounts` to sessionStorage on every change, so wiping it on
      // a transient network failure would destroy the seeded snapshot and
      // reintroduce the late-chrome pop on the next remount. Keep whatever
      // was already there (the seed, or the last successful fetch) — same
      // principle as the Drive module's isSuccess-not-isError convention.
    } finally {
      if (!silent) setLoadingAccounts(false);
    }
  }, [moduleId, token, conversationId]);

  // Listen for calendar account disconnect and toggle events to invalidate caches.
  // (#2722) H4: self-trigger guard — when this module's own
  // handleToggleAccount dispatches `integrationAccountToggled` with
  // `module: 'events'`, we already refreshed events inline. Re-running here
  // caused a brief empty-list flash. The listener now ignores events whose
  // `detail.module` matches this module.
  //
  // integrationAccountDisconnected has no `module` discriminator (it's nuclear and
  // every module reacts), so the self-trigger guard above doesn't help. The
  // disconnect handler (`handleDisconnectAccount`) already calls
  // `fetchCalendarAccounts()` and `refreshCalendarConnection()` synchronously
  // after the DELETE — but it does NOT invalidate the event-list query, so
  // we still need to fire that invalidation here. We use a ref to dedupe:
  // the local disconnect path sets a flag, and the listener honors it so we
  // don't double-invalidate when this same module triggered the disconnect.
  useEffect(() => {
    const handleIntegrationChange = (event?: Event) => {
      const detail = (event as CustomEvent | undefined)?.detail as
        | { module?: string; accountId?: string }
        | undefined;
      // Toggle events carry a `module` discriminator — skip self-dispatch.
      if (detail?.module === 'events') return;
      // Disconnect events triggered by this same component already invalidate
      // the events query inline via handleDisconnectAccount; if we wiped here
      // too, the list would briefly flash empty before refetch completed.
      if (event?.type === 'integrationAccountDisconnected' && selfDispatchedDisconnectRef.current.has(detail?.accountId ?? '')) {
        selfDispatchedDisconnectRef.current.delete(detail?.accountId ?? '');
        return;
      }
      // Other modules' disconnects / toggles: refetch in the background. Do
      // NOT wipe via setQueryData([]) — that creates a visible empty-list
      // flash. React Query keeps the stale data visible until the refetch
      // resolves, which is the desired UX for cross-module changes.
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      refreshCalendarConnection?.();
      // (#5565) Also refetch the accounts list — without this, a
      // cross-module disconnect only corrected `calendarConnected`, so a
      // just-disconnected account survived in the persisted
      // `calendarAccounts` sessionStorage snapshot into the next remount's
      // first paint (stale account still counted toward the 2+ scope-dropdown
      // gate). `handleDisconnectAccount`'s own self-dispatched path already
      // covers this for THIS module's own disconnects; this covers every
      // other module's. `silent: true` — this is a background reconcile, not
      // a user action against THIS module's accounts; it must not flash an
      // open CalendarOptionsMenu's account list to "Loading accounts…".
      fetchCalendarAccounts(false, { silent: true });
    };
    window.addEventListener('integration-oauth-connected', handleIntegrationChange);
    window.addEventListener('integrationAccountDisconnected', handleIntegrationChange);
    window.addEventListener('integrationAccountToggled', handleIntegrationChange);
    return () => {
      window.removeEventListener('integration-oauth-connected', handleIntegrationChange);
      window.removeEventListener('integrationAccountDisconnected', handleIntegrationChange);
      window.removeEventListener('integrationAccountToggled', handleIntegrationChange);
    };
  }, [queryClient, refreshCalendarConnection, fetchCalendarAccounts]);

  // Check URL params for OAuth callback - only once on mount
  const justConnectedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // calendar_connected = a Google scope grant; integration_connected = the
    // Microsoft provider-oauth callback. Either is a fresh calendar connect on
    // return (the redirect replaces the popup's {ok:true} outcome).
    if (params.get('calendar_connected') === 'true' || params.get('integration_connected') === 'true') {
      justConnectedRef.current = true;
      setCalendarConnected(true);
      params.delete('calendar_connected');
      params.delete('integration_connected');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    const error = params.get('error');
    if (error === 'oauth_denied') {
      showToast('Calendar connection was cancelled', 'error');
      params.delete('error');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } else if (error === 'oauth_failed') {
      showToast('Failed to connect calendar. Please try again.', 'error');
      params.delete('error');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []); // Only run once on mount

  // Refresh connection status when module or token changes
  useEffect(() => {
    refreshCalendarConnection();
    // Only auto-enable on fresh OAuth connect, not on every mount
    const shouldAutoEnable = justConnectedRef.current;
    if (shouldAutoEnable) {
      justConnectedRef.current = false;
    }
    fetchCalendarAccounts(shouldAutoEnable);
    fetchCalendarSyncStatus();
  }, [moduleId, token, refreshCalendarConnection, fetchCalendarAccounts, fetchCalendarSyncStatus]);

  // While a connected calendar's first sync is still in flight, poll sync-status
  // so the "syncing…" empty state EXITS once the background import lands. The
  // mount fetch alone would leave it stuck until reload (the sync runs after it).
  // Bounded (~2 min); the effect tears down as soon as initialSyncComplete flips.
  useEffect(() => {
    if (!token || !moduleId) return;
    if (!calendarConnected || calendarInitialSyncComplete) return;
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      void fetchCalendarSyncStatus();
      if (ticks >= 24) clearInterval(id); // cap at ~2 minutes (24 × 5s)
    }, 5000);
    return () => clearInterval(id);
  }, [token, moduleId, calendarConnected, calendarInitialSyncComplete, fetchCalendarSyncStatus]);

  // Calendar connect handlers (provider-agnostic via OAuth popup)
  const handleCalendarConnect = useCallback(async (provider: 'google' | 'microsoft') => {
    if (!token) return;
    try {
      // Same-window redirect via the one connect helper. No integrationId →
      // FLOW A (enroll/reconnect a calendar account): the backend keeps the
      // account picker. The post-connect work runs on RETURN, not here — the
      // mount effect above consumes calendar_connected/integration_connected →
      // justConnectedRef (auto-enable) + setCalendarConnected + refetch, and
      // App.tsx fires the centralized invalidation.
      await connectProvider({ provider, authToken: token, conversationId });
    } catch (error) {
      console.error(`Failed to connect ${provider} calendar:`, error);
      showToast(`Failed to connect ${provider} calendar`, 'error');
    }
  }, [token, conversationId, showToast]);

  const handleGoogleCalendarConnect = useCallback(() => handleCalendarConnect('google'), [handleCalendarConnect]);

  const handleCalendarSync = async () => {
    if (!token) return;
    // Prevent concurrent syncs
    if (isSyncingCalendar) {
      console.log('[EventListModule] Sync already in progress, skipping');
      return;
    }
    setIsSyncingCalendar(true);
    try {
      const startResponse = await api.http.post<{ syncId: string; status: string }>('/integrations/calendar/sync', { moduleId: moduleId }, { authToken: token });
      const { syncId } = startResponse.data;

      const maxPolls = 120; // 2 minutes max (120 * 1 second)
      const pollInterval = 1000; // 1 second
      let polls = 0;

      while (polls < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        polls++;

        let statusResponse;
        try {
          statusResponse = await api.http.get<{ syncId: string; status: string; result?: any; error?: string }>(
            `/integrations/calendar/sync/${syncId}/status`,
            { authToken: token }
          );
        } catch (pollError) {
          // The job expired out of the API's in-memory store (404). There's no
          // terminal event left, so stop polling instead of spinning to timeout,
          // tell the user, and release the sync guard.
          if (isSyncJobExpired(pollError)) {
            console.warn('[EventListModule] Calendar sync job expired (404), stopping poll');
            showToast('Sync job expired', 'error');
            setIsSyncingCalendar(false);
            break;
          }
          throw pollError;
        }

        if (statusResponse.data.status === 'completed') {
          console.log('[EventListModule] Calendar sync completed', statusResponse.data.result);
          break;
        } else if (statusResponse.data.status === 'failed') {
          throw new Error(statusResponse.data.error || 'Sync failed');
        }
      }

      if (polls >= maxPolls) {
        console.warn('[EventListModule] Sync polling timed out, but sync may still complete in background');
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      await queryClient.refetchQueries({ queryKey: queryKeys.objects.byType('event') });

      console.log('[EventListModule] Calendar sync completed, queries invalidated and refetched');
    } catch (error) {
      console.error('Failed to sync:', error);
      showToast('Failed to sync calendars', 'error');
    } finally {
      setIsSyncingCalendar(false);
      refreshCalendarConnection();
    }
  };

  // Listen for sync-calendar event from ModuleMenu (mobile 3-dots menu)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.moduleId === moduleId) {
        handleCalendarSync();
      }
    };
    window.addEventListener('xavior:sync-calendar', handler);
    return () => window.removeEventListener('xavior:sync-calendar', handler);
  }, [moduleId]);

  // Background sync (used after event changes) - silently handles rate limit errors
  const backgroundSync = useCallback(async () => {
    if (!token) return;
    // Prevent concurrent syncs
    if (isSyncingCalendar) {
      console.log('[EventListModule] Background sync skipped (sync already in progress)');
      return;
    }
    try {
      const startResponse = await api.http.post<{ syncId: string; status: string }>(
        '/integrations/calendar/sync',
        { moduleId: moduleId },
        { authToken: token }
      );
      const { syncId } = startResponse.data;

      // Shorter timeout than the foreground sync.
      const maxPolls = 60; // 1 minute max
      const pollInterval = 1000; // 1 second
      let polls = 0;

      while (polls < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        polls++;

        let statusResponse;
        try {
          statusResponse = await api.http.get<{ syncId: string; status: string; result?: any; error?: string }>(
            `/integrations/calendar/sync/${syncId}/status`,
            { authToken: token }
          );
        } catch (pollError) {
          // Background sync: the job expired (404) — stop polling silently.
          if (isSyncJobExpired(pollError)) {
            break;
          }
          throw pollError;
        }

        if (statusResponse.data.status === 'completed' || statusResponse.data.status === 'failed') {
          break;
        }
      }

      // Silently invalidate queries (no toast)
      await queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      console.log('[EventListModule] Background sync completed');
    } catch (error: any) {
      // Silently ignore rate limit errors (429) - the automatic polling will sync eventually
      if (error?.response?.status === 429 || error?.status === 429) {
        console.log('[EventListModule] Background sync rate limited (expected), will retry on next poll');
        return;
      }
      // Log other errors but don't show toast for background syncs
      console.error('[EventListModule] Background sync failed:', error);
    }
  }, [token, moduleId, conversationId, queryClient, isSyncingCalendar]);

  // Debounced background sync - waits 2 seconds after last call
  const debouncedBackgroundSyncRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedBackgroundSync = useCallback(() => {
    if (debouncedBackgroundSyncRef.current) {
      clearTimeout(debouncedBackgroundSyncRef.current);
    }
    debouncedBackgroundSyncRef.current = setTimeout(() => {
      backgroundSync();
    }, 2000); // Wait 2 seconds of inactivity before syncing
  }, [backgroundSync]);

  useEffect(() => {
    return () => {
      if (debouncedBackgroundSyncRef.current) {
        clearTimeout(debouncedBackgroundSyncRef.current);
      }
    };
  }, []);

  const handleDisconnectAccount = async (accountId: string, email: string) => {
    if (!token) return;
    try {
      // Provider-agnostic full disconnect (#3756 D4) via the canonical
      // workspace-scoped endpoint (`DELETE /workspaces/:ws/integrations/:id`,
      // same helper the email module uses). Its server-side cascade purges
      // synced data (calendar events via eventMap, email cache,
      // contacts/tasks/notes) + module links + acl_grants, then revokes tokens
      // — for BOTH Google and Microsoft (apps/api integration-disconnect.handler.ts).
      // Microsoft previously got a degraded module-unlink only; now both are
      // first-class.
      await deleteIntegration(token, accountId);
      // Mark this disconnect as self-dispatched BEFORE firing the event so
      // the listener (registered on this same component) can short-circuit
      // and avoid an empty-list flash. The handler invalidates the events
      // query directly below, so the listener has nothing extra to do for
      // self-triggered disconnects.
      selfDispatchedDisconnectRef.current.add(accountId);
      // Broadcast the disconnect + invalidate the shared cross-module account
      // caches (#2722) — same helper the IntegrationAccountsModal uses. The
      // accountId is staged above first so this component's own listener
      // short-circuits and avoids an empty-list flash.
      broadcastIntegrationDisconnect(queryClient, accountId);
      // Refresh the events list too — the disconnect removed all events synced
      // through this account on the server side. We invalidate rather than wipe
      // so any non-disconnected accounts' events stay visible until the refetch.
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      await fetchCalendarAccounts();
      await refreshCalendarConnection();
      showToast(`${email} disconnected`, 'success');
    } catch (error) {
      console.error('Failed to disconnect account:', error);
      // Clear the self-dispatch flag on failure — the disconnect didn't
      // happen, so a future retry must not be suppressed.
      selfDispatchedDisconnectRef.current.delete(accountId);
      showToast('Failed to disconnect account', 'error');
    }
  };

  const handleToggleAccount = async (accountId: string, currentlyEnabled: boolean) => {
    if (!token) return;

    // Optimistic update
    setCalendarAccounts(prev =>
      prev.map(a => a.id === accountId ? { ...a, enabledForModule: !currentlyEnabled } : a)
    );

    // When DISABLING: collapse the calendar dropdown for this account so the
    // user sees an immediate visual response (#2722). The cached `cal.selected`
    // values are preserved so re-enabling restores the prior selection set —
    // we just hide the rows that are no longer reachable from this account.
    if (currentlyEnabled) {
      setExpandedAccountIds(prev => {
        if (!prev.has(accountId)) return prev;
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }

    try {
      const scopeQuery = conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '';
      if (currentlyEnabled) {
        await api.http.delete(
          `/integrations/calendar/${accountId}/modules/${moduleId}/disable${scopeQuery}`,
          { authToken: token }
        );
      } else {
        await api.http.post(
          `/integrations/calendar/${accountId}/modules/${moduleId}/enable${scopeQuery}`,
          {},
          { authToken: token }
        );
      }
      // Refresh events and accounts. Also broadcast the toggle so sibling
      // modules (Gmail panel, Drive module, drive-module-enabled hook) react
      // — without this dispatch, toggling an account on/off from the calendar
      // picker left Gmail/Drive showing stale data until next page refresh
      // (#2722).
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      // (#2722) Cross-module broadcast. `module: 'events'` lets the Gmail
      // listener skip its `sync-now` side effect (a calendar-account toggle
      // doesn't need a Gmail sync), and lets this module's own listener
      // skip the redundant events-cache invalidation we already did above.
      window.dispatchEvent(new CustomEvent('integrationAccountToggled', {
        detail: { accountId, enabled: !currentlyEnabled, module: 'events' },
      }));
      await fetchCalendarAccounts();
      await refreshCalendarConnection();
    } catch (error) {
      console.error('Failed to toggle calendar account:', error);
      // Revert on failure
      setCalendarAccounts(prev =>
        prev.map(a => a.id === accountId ? { ...a, enabledForModule: currentlyEnabled } : a)
      );
    }
  };
  // Calendar selection state (per-account expandable calendar lists)
  // Track which accounts user has explicitly collapsed (persisted in localStorage)
  const collapsedStorageKey = `xavior:calendar-collapsed:${moduleId}`;
  const getCollapsedAccountIds = useCallback((): Set<string> => {
    try {
      const stored = localStorage.getItem(collapsedStorageKey);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  }, [collapsedStorageKey]);
  const [expandedAccountIds, setExpandedAccountIds] = useState<Set<string>>(new Set());
  const [accountCalendars, setAccountCalendars] = useState<Record<string, { calendars: Array<{ id: string; name: string; primary: boolean; accessRole: string; color: string | null; selected: boolean }>; loading: boolean; error: string | null }>>({});
  // When a user unchecks a calendar, immediately filter events whose
  // providerCalendarId is in this Set so the screen drops them without waiting
  // for the React Query refetch round-trip. Entries auto-clear after a TTL
  // long enough to cover the network round-trip + cache invalidation, short
  // enough that re-checking surfaces events again instantly.
  const [recentlyUncheckedCalendarIds, setRecentlyUncheckedCalendarIds] = useState<Set<string>>(new Set());

  // All writable calendars across accounts (for new event calendar picker)
  // Only includes selected calendars; only the first primary is marked as such
  const allWritableCalendars = useMemo(() => {
    const cals: Array<{ id: string; name: string; primary: boolean; accessRole: string }> = [];
    let foundPrimary = false;
    for (const acc of Object.values(accountCalendars)) {
      for (const cal of acc.calendars) {
        if (!cal.selected) continue;
        if ((cal.accessRole === 'owner' || cal.accessRole === 'writer') && cal.id.includes('@')) {
          const isPrimary = cal.primary && !foundPrimary;
          if (isPrimary) foundPrimary = true;
          cals.push({ ...cal, primary: isPrimary });
        }
      }
    }
    // Primary first, then alphabetical
    cals.sort((a, b) => {
      if (a.primary && !b.primary) return -1;
      if (!a.primary && b.primary) return 1;
      return a.name.localeCompare(b.name);
    });
    return cals;
  }, [accountCalendars]);

  const fetchCalendarsForAccount = useCallback(async (accountId: string) => {
    if (!token) return;
    setAccountCalendars(prev => ({
      ...prev,
      [accountId]: { calendars: prev[accountId]?.calendars || [], loading: true, error: null }
    }));
    try {
      const response = await api.http.get<{ calendars: Array<{ id: string; name: string; primary: boolean; accessRole: string; color: string | null; selected: boolean }> }>(
        `/integrations/calendar/${accountId}/calendars?moduleId=${encodeURIComponent(moduleId)}`,
        { authToken: token }
      );
      // Sort: primary first, then owner calendars, then others
      const sorted = [...response.data.calendars].sort((a, b) => {
        if (a.primary && !b.primary) return -1;
        if (!a.primary && b.primary) return 1;
        const aOwner = a.accessRole === 'owner' || a.accessRole === 'writer';
        const bOwner = b.accessRole === 'owner' || b.accessRole === 'writer';
        if (aOwner && !bOwner) return -1;
        if (!aOwner && bOwner) return 1;
        return a.name.localeCompare(b.name);
      });
      setAccountCalendars(prev => ({
        ...prev,
        [accountId]: { calendars: sorted, loading: false, error: null }
      }));
    } catch (error) {
      console.error('Failed to fetch calendars:', error);
      setAccountCalendars(prev => ({
        ...prev,
        [accountId]: { calendars: [], loading: false, error: 'Failed to load calendars' }
      }));
    }
  }, [token, moduleId]);

  const handleToggleAccountExpand = useCallback((accountId: string) => {
    setExpandedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
        // User collapsed — remember it
        const collapsed = getCollapsedAccountIds();
        collapsed.add(accountId);
        try { localStorage.setItem(collapsedStorageKey, JSON.stringify([...collapsed])); } catch { /* ignore */ }
      } else {
        next.add(accountId);
        // User expanded — remove from collapsed
        const collapsed = getCollapsedAccountIds();
        collapsed.delete(accountId);
        try { localStorage.setItem(collapsedStorageKey, JSON.stringify([...collapsed])); } catch { /* ignore */ }
      }
      return next;
    });
    // Fetch calendars only on first expand (data persists across collapse/expand)
    if (!accountCalendars[accountId]?.calendars?.length && !accountCalendars[accountId]?.loading) {
      fetchCalendarsForAccount(accountId);
    }
  }, [accountCalendars, fetchCalendarsForAccount, getCollapsedAccountIds, collapsedStorageKey]);

  // Eagerly fetch calendars for all enabled accounts (for calendar picker dropdown)
  // (#5565) Gated on `!loadingAccounts` — `calendarAccounts` can now be
  // non-empty on the very first render (seeded from the sessionStorage
  // snapshot, see above), so without this guard this effect would fire
  // account-scoped GETs against a POSSIBLY STALE seeded account list before
  // the real fetch has reconciled it. Mirrors the scope-reset effect's
  // existing `loadingAccounts` guard above.
  useEffect(() => {
    if (loadingAccounts || calendarAccounts.length === 0) return;
    for (const acc of calendarAccounts) {
      if (acc.enabledForModule && !accountCalendars[acc.id]?.calendars?.length && !accountCalendars[acc.id]?.loading) {
        fetchCalendarsForAccount(acc.id);
      }
    }
  }, [loadingAccounts, calendarAccounts, fetchCalendarsForAccount, accountCalendars]);

  // Auto-expand all accounts (except user-collapsed ones) when menu opens
  // (#5565) Same `loadingAccounts` guard as above — the menu can be opened
  // (or already open on remount) while `calendarAccounts` still holds the
  // seeded snapshot; wait for the real fetch to settle before expanding.
  useEffect(() => {
    if (loadingAccounts || !showOptionsMenu || calendarAccounts.length === 0) return;
    const collapsed = getCollapsedAccountIds();
    const toExpand = calendarAccounts.filter(a => !collapsed.has(a.id)).map(a => a.id);
    if (toExpand.length === 0) return;
    setExpandedAccountIds(new Set(toExpand));
    // Auto-fetch calendars for newly expanded accounts
    for (const accId of toExpand) {
      if (!accountCalendars[accId]?.calendars?.length && !accountCalendars[accId]?.loading) {
        fetchCalendarsForAccount(accId);
      }
    }
  }, [loadingAccounts, showOptionsMenu, calendarAccounts, getCollapsedAccountIds, fetchCalendarsForAccount]);

  const handleToggleCalendar = useCallback(async (accountId: string, calendarId: string, calendarName: string, currentlySelected: boolean) => {
    if (!token) return;
    // Optimistic update
    setAccountCalendars(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        calendars: (prev[accountId]?.calendars || []).map(cal =>
          cal.id === calendarId ? { ...cal, selected: !currentlySelected } : cal
        )
      }
    }));
    // When UNCHECKING: immediately drop matching events from the rendered
    // list via predicate filter. Mirrors Gmail's account-toggle behavior so
    // the user sees the change instantly rather than waiting for the
    // React Query refetch to land.
    const isUnchecking = currentlySelected;
    if (isUnchecking) {
      setRecentlyUncheckedCalendarIds(prev => {
        const next = new Set(prev);
        next.add(calendarId);
        return next;
      });
    } else {
      // Re-check: drop the calendar from the unchecked-set immediately so any
      // cached events that match it become visible again without a refetch.
      setRecentlyUncheckedCalendarIds(prev => {
        if (!prev.has(calendarId)) return prev;
        const next = new Set(prev);
        next.delete(calendarId);
        return next;
      });
    }
    try {
      await api.http.put(
        `/integrations/calendar/${accountId}/calendars/${encodeURIComponent(calendarId)}`,
        { selected: !currentlySelected, calendarName, moduleId: moduleId },
        { authToken: token }
      );
      // Refresh events list after preference is saved
      await queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      // Once the server-side detach has propagated, drop this calendar from
      // the unchecked-set — the refetched data is now authoritative. (For
      // re-check, this is a no-op since we already cleared above.)
      if (isUnchecking) {
        setRecentlyUncheckedCalendarIds(prev => {
          if (!prev.has(calendarId)) return prev;
          const next = new Set(prev);
          next.delete(calendarId);
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to toggle calendar:', error);
      // Revert optimistic update
      setAccountCalendars(prev => ({
        ...prev,
        [accountId]: {
          ...prev[accountId],
          calendars: (prev[accountId]?.calendars || []).map(cal =>
            cal.id === calendarId ? { ...cal, selected: currentlySelected } : cal
          )
        }
      }));
      // Restore visibility on failure: if we'd hidden them, drop the entry;
      // if we'd shown them (re-check failed), they're already visible.
      if (isUnchecking) {
        setRecentlyUncheckedCalendarIds(prev => {
          if (!prev.has(calendarId)) return prev;
          const next = new Set(prev);
          next.delete(calendarId);
          return next;
        });
      }
      showToast('Failed to update calendar selection', 'error');
    }
  }, [token, showToast, queryClient, conversationId, moduleId]);

  // Defense-in-depth: if the post-success cleanup never lands (e.g. the
  // invalidation rejected silently), entries still self-clear after 30s so
  // we don't pin events as hidden indefinitely.
  useEffect(() => {
    if (recentlyUncheckedCalendarIds.size === 0) return;
    const timer = setTimeout(() => {
      setRecentlyUncheckedCalendarIds(new Set());
    }, 30000);
    return () => clearTimeout(timer);
  }, [recentlyUncheckedCalendarIds]);

  return {
    // Connection / sync status
    calendarConnected,
    isSyncingCalendar,
    calendarInitialSyncComplete,
    calendarSyncError,
    calendarLastSyncedAt,
    calendarLastSyncLabel,
    // Accounts + scope
    calendarAccounts,
    loadingAccounts,
    calendarScope,
    setCalendarScope,
    // Prompts / modals
    skipCalendarPrompt,
    setSkipCalendarPrompt,
    showSyncExplanationModal,
    setShowSyncExplanationModal,
    // Per-account calendar lists
    expandedAccountIds,
    accountCalendars,
    recentlyUncheckedCalendarIds,
    allWritableCalendars,
    // Handlers
    handleCalendarConnect,
    handleGoogleCalendarConnect,
    handleCalendarSync,
    debouncedBackgroundSync,
    handleDisconnectAccount,
    handleToggleAccount,
    handleToggleCalendar,
    handleToggleAccountExpand,
  };
}
